import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types'
import * as types from '../../constants/actionTypes';
import { actions as toolTipActions } from '../../actions/toolTipActions';
import { DATE_FORMAT } from '../../constants/constants';
import Drawer from '../Common/Drawer/Drawer';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import { Editor } from 'react-draft-wysiwyg';
import Toggle from '../Common/Toggle/Toggle';
import DatePicker from '../Common/DatePicker/DatePicker';
import { convertToRaw } from 'draft-js';
import Moment from 'moment';
import TextArea from '../Common/TextArea';

export class ToolTipDrawer extends Component {
    constructor(props, context) {
        super(props, context);

        let toolTip = {
            id: 0,
            title: '',
            message: '',
            startDate: '',
            expirationDate: '',
            routeId: '',
            isActive: true,
        }

        this.state = {
            isFormValid: true,
            isShowTitleMessageRequired: false,
            isShowMessageMessageRequired: false,
            isShowDateRequired: false,
            isShowDateError: false,
            isShowRouteRequired: false,
            isLoading: false,
            toolTip: (this.props.toolTip) ? this.loadToolTip(toolTip) : toolTip,
            blankToolTip: toolTip
        };

    }

    componentDidUpdate(prevProps) {
        if (this.props.toolTip && this.props.toolTip.id !== prevProps.toolTip.id) {
            this.setState({ isLoading: true }, () => this.loadToolTip(this.state.blankToolTip, true));
        }
    }

    loadToolTip = (blankToolTip, setState) => {
        /* There is some funky stuff going on here to get the ux we wanted with the WYSIWYG editors

        * The most important thing to note, and the reason for these weird workarounds, is that defaultContentState on the WYSIWYG editors only fires once (hence the word default)
        * So in order to display the title and message when editing a tooltip you must not feed the editor a blank string initially
        * The value of defaultContentState has to be the value that you want displayed when the editor component is initially rendered
        * 
        * Additionally, we wanted to have the ability to select a new tooltip in the list while the drawer is open and have it load the newly selected tooltip without
        * having to close the drawer and open a new one. To do this I had to add hideWhileLoading to the loading indicator so when
        * componentDidUpdate fires and a new tooltip is detected, the editors are removed from the screen so we can pass the defaultContentState we want displayed
        */ 

        let tt = {...blankToolTip};
        const newToolTip = this.props.toolTip;

        tt.id = newToolTip.id;
        tt.isActive = newToolTip.isActive;
        tt.title = newToolTip.title;
        tt.message = newToolTip.message;
        tt.expirationDate = (newToolTip.expirationDate) ? Moment.utc(newToolTip.expirationDate).format(DATE_FORMAT) : '';
        tt.startDate = Moment.utc(newToolTip.startDate).format(DATE_FORMAT);
        tt.routeId = newToolTip.routeId;

        if(setState){
            this.setState({ toolTip: tt, isLoading: false });
        } else {
            return tt;
        }
    }

    validateForm = () => {
        const toolTip = this.state.toolTip;
        let isShowTitleMessageRequired = false;
        let isShowMessageMessageRequired = false;
        let isShowDateRequired = false;
        let isShowDateError = false;
        let isShowRouteRequired = false;

        if(!toolTip.title){
            isShowTitleMessageRequired = true;        
        }

        if(!toolTip.message){
            isShowMessageMessageRequired = true;
        } else {
            const messageMessageJson = JSON.parse(toolTip.message);
            if(messageMessageJson.blocks[0].text === ""){
                isShowMessageMessageRequired = true;
            }            
        }

        if(toolTip.startDate == ""){
            isShowDateRequired = true;
        }

        const start = Moment(toolTip.startDate);
        const target = Moment(toolTip.expirationDate);
        const diffDays = Moment.duration(start.diff(target)).as("days");

        if(diffDays > 0) {
            isShowDateError = true;
        }

        if(toolTip.routeId == 0 || toolTip.routeId == ''){
            isShowRouteRequired = true;
        }

        const isFormValid = (!isShowDateError && !isShowDateRequired && !isShowRouteRequired
            && !isShowTitleMessageRequired && !isShowTitleMessageRequired);

        this.setState({
            isFormValid: isFormValid,
            isShowTitleMessageRequired: isShowTitleMessageRequired,
            isShowMessageMessageRequired: isShowMessageMessageRequired,
            isShowDateRequired: isShowDateRequired,
            isShowDateError: isShowDateError,
            isShowRouteRequired: isShowRouteRequired
        });
        return isFormValid;
    }

    onSave = async () => {
        if(this.validateForm()){
            try {
                this.setState({ isLoading: true });
                const response = await this.props.toolTipActions.save(this.state.toolTip);
                if (response.type === types.CREATE_TOOL_TIPS_SUCCESS || types.UPDATE_TOOL_TIPS_SUCCESS) {
                    this.props.onSave();
                } else if (response.type === types.CREATE_TOOL_TIPS_FAILURE || types.UPDATE_TOOL_TIPS_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                    this.setState({ isLoading: false });
                }
            } catch (error) {
                Notification("error", "Error", error);
                this.setState({ isLoading: false });
            }            
        }
    }

    onFormChange = (name, value) => {
        this.setState({ toolTip: { ...this.state.toolTip, [name]: value } });
    }

    onRouteChange = (id) => {
        this.onFormChange("routeId", id);
    }

    onTitleChange = (e) => {
        this.onFormChange(e.target.name, e.target.value);
    }

    onMessageChange = (editorState) => {
        const messageJson = convertToRaw(editorState.getCurrentContent());
        this.setState({ toolTip: { ...this.state.toolTip, message: JSON.stringify(messageJson) }})
    }

    onToggleChange = (name, isChecked) => {
        this.onFormChange(name, isChecked);
    }

    onStartDateChange = (datetime) => {
        this.onFormChange("startDate", datetime);
    }

    onExpDateChange = (datetime) => {
        this.onFormChange("expirationDate", datetime);
    }

    render() {
        let toolbar = {
            options: ['inline', 'fontSize', 'fontFamily', 'textAlign', 'colorPicker', 'link', 'emoji'],
            inline: {
                options: ['bold', 'italic', 'underline']
            }
        };

        let messageMessageJson = null;
        try{
            messageMessageJson = JSON.parse(this.state.toolTip.message);
        } catch(error) {
            // ignore error
        }

        return (
            <Drawer isWide={true} title="Tool Tip"
                onSaveClick={this.onSave}
                onCancelClick={this.props.onCancel}
            >
                <LoadingIndicator isLoading={this.state.isLoading} hideWhileLoading={true}>
                    <form noValidate>
                        <div>
                            <strong>Title</strong>
                            <TextArea
                                name="title"
                                rows={4}
                                maxLength={200}
                                value={this.state.toolTip.title}
                                onChange={this.onTitleChange}
                                isRequired={true}
                            />
                        </div>
                        {this.state.isShowTitleMessageRequired && !this.state.isFormValid &&
                            <div className="text-danger">Title is required</div>
                        }
                        <div className="mt-4">
                            <strong>Message</strong>
                            <Editor
                                spellCheck={true}
                                toolbarClassName="toolbarClassName"
                                wrapperClassName="wrapperClassName"
                                editorClassName="editorClassName"
                                toolbar={toolbar}
                                onEditorStateChange={this.onMessageChange}
                                defaultContentState={messageMessageJson}
                            />
                        </div>   
                        {this.state.isShowMessageMessageRequired && !this.state.isFormValid &&
                            <div className="text-danger">Message is required</div>
                        }                     
                        <div className="row mt-4">
                            <div className="col-6">
                                <DatePicker showIcon value={this.state.toolTip.startDate} name="startDate" label="Start Date" onChange={this.onStartDateChange} />
                            </div>
                            <div className="col-6">
                                <DatePicker showIcon value={this.state.toolTip.expirationDate} name="expirationDate" label="Expiration Date" onChange={this.onExpDateChange} />
                            </div>
                        </div>
                        {this.state.isShowDateRequired && !this.state.isFormValid &&
                            <div className="text-danger">Start Date is required</div>
                        }
                        {this.state.isShowDateError && !this.state.isFormValid &&
                            <div className="text-danger">Start Date must come before the Expiration Date</div>
                        }
                        <div className="mt-2">
                            <MultiSelectBox 
                                name="routeId" 
                                label="Route" 
                                idField="id" 
                                valueField="name" 
                                options={this.props.routes} 
                                selectedValues={this.state.toolTip.routeId.toString()} 
                                onChange={this.onRouteChange} 
                                isRequired={false} 
                                isMulti={false}
                            />
                        </div>
                        {this.state.isShowRouteRequired && !this.state.isFormValid &&
                            <div className="text-danger">Route is required</div>
                        }
                        <div className="mt-4">
                            <Toggle name="isActive" text="Subscription Active" checked={this.state.toolTip.isActive} onChange={this.onToggleChange} />
                        </div>
                    </form>
                </LoadingIndicator>
            </Drawer>
        )
    } 
}

ToolTipDrawer.propTypes = {
    toolTip: PropTypes.object,
    routes: PropTypes.array.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    toolTipActions: PropTypes.object.isRequired
}

function mapDispatchToProps(dispatch){
    return {
        toolTipActions: bindActionCreators(toolTipActions, dispatch)
    }
}

export default connect(null, mapDispatchToProps)(ToolTipDrawer);